// TelcoTrack — typ čísla domu: popisné („č.p.“) vs evidenční („č.ev.“).
// SSOT pro prefix v labelech, párovací klíče a detekci typu z volného textu.
// Zarovnáno s KN `cp_ce` (1 = č.p., 2 = č.e.) a sloupcem `address_points.typ_cisla`.

/** `P` = číslo popisné, `E` = číslo evidenční. */
export type TypCisla = 'P' | 'E';

export const TYP_CISLA_POPISNE: TypCisla = 'P';
export const TYP_CISLA_EVIDENCNI: TypCisla = 'E';

/** Prefix pro zobrazení čísla domu. */
export const TYP_CISLA_PREFIX: Record<TypCisla, string> = {
  P: 'č.p.',
  E: 'č.ev.',
};

/** Sloupec, který v jedné buňce ukazuje číslo obou typů. */
export const TYP_CISLA_COLUMN_LABEL = 'č.p. / č.ev.';

/** Volný text → `P` / `E`; `null` když hodnota není známý typ. */
export function normalizeTypCisla(value: unknown): TypCisla | null {
  if (typeof value !== 'string') return null;
  const v = value.trim().toUpperCase();
  if (v === 'P' || v === 'E') return v;
  return null;
}

/** Typ s fallbackem na `P` (výchozí hodnota sloupce v DB). */
export function typCislaOrDefault(value: unknown): TypCisla {
  return normalizeTypCisla(value) ?? TYP_CISLA_POPISNE;
}

/** KN `cp_ce`: 1 → `P`, 2 → `E`; jinak `null`. */
export function typCislaFromKnCpCe(cpCe: unknown): TypCisla | null {
  const raw = typeof cpCe === 'number' ? String(cpCe) : (cpCe ?? '').toString().trim();
  if (raw === '1') return TYP_CISLA_POPISNE;
  if (raw === '2') return TYP_CISLA_EVIDENCNI;
  return null;
}

/**
 * „č.ev.“ / „č.e.“ / „číslo evidenční“ kdekoliv v textu (RÚIAN alternativeIdentifier,
 * KN label stavby, ROPID `cela_adresa`, poznámky).
 */
const EVIDENCNI_RE = /[čc]\s*\.\s*ev?\s*\.?\s*(?=\d)|evidenčn|evidencn/i;

/** „č.p.“ / „popisné“ kdekoliv v textu. */
const POPISNE_RE = /[čc]\s*\.\s*p\s*\.?\s*(?=\d)|popisn/i;

/**
 * Typ čísla z volného textu (RÚIAN alternativeIdentifier, KN label stavby, ROPID adresa, poznámka).
 * Vrací `null` když text typ neurčuje **nebo** zmiňuje oba — např. VDP popisek
 * „Číslo popisné nebo evidenční“. Volající typ nikdy nesmí vymýšlet.
 */
export function parseTypCislaFromText(text: string | null | undefined): TypCisla | null {
  const s = (text ?? '').trim();
  if (!s) return null;
  const evidencni = EVIDENCNI_RE.test(s);
  const popisne = POPISNE_RE.test(s);
  if (evidencni === popisne) return null;
  return evidencni ? TYP_CISLA_EVIDENCNI : TYP_CISLA_POPISNE;
}

/**
 * Číslo domu s prefixem: `č.p. 23/5`, `č.ev. 16`.
 * Prázdné číslo → `''` (volající řeší fallback na parcelu).
 */
export function formatHouseNumberLabel(
  typCisla: TypCisla | string | null | undefined,
  cisloPopisne: string | null | undefined,
  orientacni?: string | null,
): string {
  const cislo = (cisloPopisne ?? '').trim();
  if (!cislo) return '';
  const orient = (orientacni ?? '').trim();
  const prefix = TYP_CISLA_PREFIX[typCislaOrDefault(typCisla)];
  return orient ? `${prefix} ${cislo}/${orient}` : `${prefix} ${cislo}`;
}

/** Prefix samotný — pro místa, kde se číslo skládá jinak (Drive názvy, exporty). */
export function typCislaPrefix(typCisla: TypCisla | string | null | undefined): string {
  return TYP_CISLA_PREFIX[typCislaOrDefault(typCisla)];
}

/**
 * Odstraní prefix č.p./č.ev. z hodnoty, která už může být naformátovaná
 * („č.ev. 16“ → „16“). Používá se před uložením digits-only do `cislo_popisne`.
 */
export function stripHouseNumberPrefix(value: string | null | undefined): string {
  return (value ?? '')
    .replace(/^\s*(?:č|c)\.?\s*(?:p|ev?)\.?\s*/i, '')
    .trim();
}
